import React, { useContext, useEffect, useState } from 'react'
import PropTypes from 'prop-types'

import { Game } from '../../atomic-components/organisms/game'
import { GameDescription } from '../../atomic-components/organisms/gameDescription'
import { Layout } from '../../atomic-components/organisms/layout'
import { Seo } from '../../atomic-components/organisms/seo'
import { DescriptionsList } from '../../atomic-components/molecules/descriptionsList'
import {
  ContentUnWrapper,
  ContentWrapper,
} from '../../atomic-components/atoms/contentWrapper'
import { If } from '../../atomic-components/atoms/if'
import { LoggedOutHeader } from '../../atomic-components/atoms/loggedOutHeader'
import { Title } from '../../atomic-components/atoms/text'
import { ResponsiveCSSContainer } from '../../atomic-components/atoms/responsiveContainerCSS'

import { AuthContext } from '../../context/authProvider'
import { useEventTracking } from '../../context/eventTrackingProvider'

import { themeSizes, themeStyles } from '../../styles/theme'
import useSlugs from '../../hooks/useSlugs'
import { useLocation } from '@reach/router'
import { navigate } from 'gatsby'
import { isItemActive } from '../../dataManipulation/cmsItems'
import useQueryParams from '../../hooks/useQueryParams'
import { shouldExcludeByGeoLocation } from '../../utils/shouldExcludeByGeoLocation'
import useScreenWidth from '../../hooks/useScreenWidth'
import useIsLandscape from '../../hooks/useIsLandscape'
import { useSEOContentVisibility } from '../../hooks/useSEOContentVisibility'
import storageService from '../../services/storageService'
import {
  normalizeDataLayerText,
  pushDataLayer,
} from '../../utils/pushDataLayer'
import { isMobileApp } from '../../utils/mobileAppFunctionality'
import { useFeatureValue } from '@growthbook/growthbook-react'
import { InGameBottomNavigation } from '../../atomic-components/organisms/InGameBottomNavigation'
import { useIsInGameNavigationEnabled } from '../../hooks/useIsInGameNavigationEnabled'

const CasinoGame = ({ pageContext }) => {
  const {
    title,
    seo,
    logged_out_description,
    logged_out_title,
    maintenance_mode,
    exclude_by_geo_location,
    disable_play_for_fun,
    gameType,
    game_sub_type,
    providerSlug,
    slug
  } = pageContext
  const { isLoggedIn, accessCountry } = useContext(AuthContext)
  const { updateCurrentGameId, updateCurrentGameType } = useEventTracking()
  const location = useLocation()
  const params = useQueryParams()
  const [loginSlug, casinoSlug] = useSlugs(['login', 'casino'])
  const { width } = useScreenWidth(0)

  const [didReadSEO, setDidReadSEO] = useState(false)
  const [showCashbackDrawer, setShowCashbackDrawer] = useState(0)
  const { showSEOContent } = useSEOContentVisibility()
  const restrictGameClickInDevelop = useFeatureValue('restrict_gameclick_in_develop_mode')
  const isInGameBottomNavigationEnabled = useIsInGameNavigationEnabled()

  const isUnderMaintenance =
    maintenance_mode?.active_from &&
    isItemActive({
      activeFrom: maintenance_mode.active_from,
      activeTo: maintenance_mode.active_to,
    })

  const hasWindow = typeof window !== 'undefined'
  const isDesktop = width >= themeSizes.desktop
  const isMobile = width < themeSizes.desktop && width > 0
  const showMobileGame = (isLoggedIn || didReadSEO) && isMobile
  const showDesktopGame =
    isLoggedIn || (!isUnderMaintenance && !disable_play_for_fun)
  const env = process.env.GATSBY_ENV
  const isLandscape = useIsLandscape(isMobile)
  const shouldShowInGameBottomNavigation =
    isInGameBottomNavigationEnabled && !isLandscape
  const isBlockedByGeoLocation = shouldExcludeByGeoLocation(
    exclude_by_geo_location,
    accessCountry,
    isLoggedIn
  )

  const gameEventData = {
    location: `${gameType}_game`,
    provider_name: providerSlug,
    game_id: slug,
    game_name: normalizeDataLayerText(title),
  }

  const toggleCashbackDrawer = () => {
    setShowCashbackDrawer((prev) => prev + 1)
  }

  const screenName = 'casino_game'

  useEffect(() => {
    window.scrollTo({ top: 0 })
    pushDataLayer({
      eventName: 'screen-loaded',
      data: {
        screen_name: screenName,
        game_id: gameEventData?.game_id,
        game_name: gameEventData?.game_name,
        provider_name: gameEventData?.provider_name,
      },
    })
  }, [])

  useEffect(() => {
    if (!isLoggedIn || isMobileApp() || (!restrictGameClickInDevelop && env !== 'prod')) return
    
    const locationState = storageService.getValue('geoStatus')

    if (locationState !== 'granted') { 
      navigate(`/${casinoSlug}`, { state: { triggerGeolocation: true } })
    }
  }, [isLoggedIn])

  useEffect(() => {
    if (params?.redirect === 'login') {
      navigate(`/${loginSlug}`, { state: { redirectURL: location?.pathname } })
    }
  }, [location])

  useEffect(() => {
    if (slug) {
      updateCurrentGameId(slug)
      updateCurrentGameType(gameType)
    }

    return () => {
      updateCurrentGameId(null)
      updateCurrentGameType(null)
    }
  }, [slug, updateCurrentGameId, updateCurrentGameType])

  const createDesktopSeo = () => {
    if (!isDesktop && hasWindow) return <></>
    return (
      <ContentWrapper gap="1em">
        <GameDescription
          game={pageContext}
          setDidReadSEO={setDidReadSEO}
          isUnderMaintenance={isUnderMaintenance}
          disablePlayForFun={disable_play_for_fun}
        />
      </ContentWrapper>
    )
  }

  const createDesktopGame = () => {
    if (!isDesktop && hasWindow) return <></>
    return (
      <>
        <Game
          key="game"
          context={pageContext}
          toggleCashbackDrawer={toggleCashbackDrawer}
          isUnderMaintenance={isUnderMaintenance}
          isBlockedByGeoLocation={isBlockedByGeoLocation}
          gameEventData={gameEventData}
        />
        <If
          condition={isLoggedIn}
          render={() => (
            <ContentWrapper>
              <Title
                style={{
                  color: themeStyles.colors.white.main,
                  padding: '32px 0 16px 0',
                  textAlign: 'center',
                }}
              >
                {title}
              </Title>
            </ContentWrapper>
          )}
        />
      </>
    )
  }

  const createMobileSeo = () => {
    if (!isMobile && hasWindow) return <></>
    return (
      <GameDescription
        game={pageContext}
        setDidReadSEO={setDidReadSEO}
        isUnderMaintenance={isUnderMaintenance}
        disablePlayForFun={disable_play_for_fun}
      />
    )
  }

  const createMobileGame = () => {
    if (!isMobile && hasWindow) return <></>
    return (
      <>
        <Game
          key="game"
          context={pageContext}
          isUnderMaintenance={isUnderMaintenance}
          isBlockedByGeoLocation={isBlockedByGeoLocation}
          gameEventData={gameEventData}
          isInGameBottomNavigationEnabled={isInGameBottomNavigationEnabled}
          isMobile
        />
        <If
          condition={shouldShowInGameBottomNavigation}
          render={() => (
            <InGameBottomNavigation
              gameType={gameType}
              game_sub_type={game_sub_type}
            />
          )}
        />
      </>
    )
  }

  const hideDescription = !showSEOContent

  return (
    <Layout
      hideHeader={isMobile && (didReadSEO || isLoggedIn)}
      hideFooter={isMobile}
      triggerCashbackDrawer={showCashbackDrawer}
      eventLocation={screenName}
      dontShowPopups
    >
      <ContentWrapper>
        <LoggedOutHeader
          description={logged_out_description}
          title={logged_out_title || title}
          hideBackButton={isDesktop && showDesktopGame}
          hideDescription={hideDescription}
        />
      </ContentWrapper>
      <ResponsiveCSSContainer showMobile>
        <If
          condition={showMobileGame}
          render={createMobileGame}
          renderElse={createMobileSeo}
        />
      </ResponsiveCSSContainer>
      <ResponsiveCSSContainer showDesktop>
        <If
          condition={showDesktopGame}
          render={createDesktopGame}
          renderElse={createDesktopSeo}
        />
      </ResponsiveCSSContainer>
      <If
        condition={!showMobileGame && showSEOContent}
        render={() => (
          <ContentWrapper>
            <ContentUnWrapper addPaddingToChild={isDesktop}>
              <DescriptionsList
                fallbackTitle={title}
                content={(seo || []).map((row) => ({
                  title: row.seo_title,
                  description: row.seo_description,
                }))}
              />
            </ContentUnWrapper>
          </ContentWrapper>
        )}
      />
    </Layout>
  )
}

CasinoGame.propTypes = {
  pageContext: PropTypes.shape({
    title: PropTypes.string,
    seo: PropTypes.array,
    providerName: PropTypes.string,
    logged_out_description: PropTypes.string,
    logged_out_title: PropTypes.string,
    yoast_head: PropTypes.object,
    disable_play_for_fun: PropTypes.bool,
    exclude_by_geo_location: PropTypes.array,
    maintenance_mode: PropTypes.shape({
      active_to: PropTypes.number,
      active_from: PropTypes.number,
    }),
  }),
}

export default CasinoGame

export const Head = ({
  pageContext: { title, yoast_head, href_langs },
  location: { pathname },
}) => {
  const yoastContent = { yoast_head_json: yoast_head?.json || {} }

  return (
    <Seo
      title={title}
      yoast={yoastContent}
      href_langs={href_langs}
      pathname={pathname}
    />
  )
}
